import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type SyntheticEvent } from 'react';

import { useTranslation } from 'react-i18next';
import { deckKeys, reviewQueueKey } from '@/entities/deck';
import { createCard } from '@/shared/api/decks';
import { Button, ErrorMessage, TextArea, TextField } from '@/shared/ui';

import styles from './DeckEditor.module.css';

const empty = { front: '', back: '', source_excerpt: '', source_url: '' };

/**
 * Adds a question and its answer, optionally with the source it came from.
 *
 * The form is cleared only if it still holds the card that was sent: someone
 * who starts the next card while the previous one is saving keeps their text.
 */
export function AddCardForm({ deckId }: { deckId: string }): React.JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [card, setCard] = useState(empty);
  const frontRef = useRef<HTMLTextAreaElement>(null);

  const mutation = useMutation({
    mutationFn: (input: typeof empty) => createCard(deckId, input),
    onSuccess: async (_created, submitted) => {
      setCard((current) => (current === submitted ? empty : current));
      frontRef.current?.focus();
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: deckKeys.one(deckId) }),
        queryClient.invalidateQueries({ queryKey: deckKeys.all, exact: true }),
        queryClient.invalidateQueries({ queryKey: reviewQueueKey }),
      ]);
    },
  });

  const set =
    (field: keyof typeof empty) =>
    (event: { target: { value: string } }): void => {
      const { value } = event.target;
      setCard((current) => ({ ...current, [field]: value }));
    };

  const submit = (event: SyntheticEvent): void => {
    event.preventDefault();
    mutation.mutate(card);
  };

  return (
    <form className={styles.form} onSubmit={submit}>
      <TextArea
        ref={frontRef}
        label={t('decks.front')}
        required
        maxLength={10000}
        value={card.front}
        onChange={set('front')}
      />
      <TextArea
        label={t('decks.back')}
        required
        maxLength={10000}
        value={card.back}
        onChange={set('back')}
      />
      <details className={styles.source}>
        <summary>{t('decks.source')}</summary>
        <TextArea
          label={t('decks.sourceExcerpt')}
          maxLength={10000}
          value={card.source_excerpt}
          onChange={set('source_excerpt')}
        />
        <TextField
          label={t('decks.sourceUrl')}
          type="url"
          placeholder="https://"
          maxLength={2048}
          value={card.source_url}
          onChange={set('source_url')}
        />
      </details>
      {mutation.isError && <ErrorMessage error={mutation.error} />}
      <Button type="submit" variant="primary" disabled={mutation.isPending}>
        {t('decks.addCard')}
      </Button>
    </form>
  );
}
